import React, { forwardRef, useCallback, useEffect, useState, useRef, useImperativeHandle } from 'react';
import Dropzone from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useDispatch } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { isEmpty, cloneDeep, includes, some } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as action from '../../Pages/DevlopmentOrder/store/DevelopmentOrder.action';
import noImage from '../../assets/images/no-image.png';
import ImportFileModal from '../../Pages/DevlopmentOrder/DesignDetail/ImportFileModal';
import { toasterMsg } from '../Toaster/Toaster';
import { isBase64Image, imageUrl } from '../helper';
import { NewCheckbox } from '../PIM/common/NewCheckbox';
import { UploadIcon, DeleteIcon, TickIcon } from '../../bombiix-icon';
import { BombiixModal } from '../BombiixModal';
import { resolveImageLoader } from './image-source-loader';
import { BombiixDropzone } from '../BombiixDropzone';

const cropped = (cropSize) => {
  return cropSize === '4/3' ? 4 / 7 : 1;
};

/**
 * Determines the correct image path handling both S3 bucket and assets folder images
 * @param {string} image - The image path or identifier
 * @returns {string} - The full image URL
 */
const getImagePath = (image) => {
  if (!image) return '';

  if (image.includes('data:image/') || image.includes('data:video/')) {
    return image;
  }

  if (image.includes('https://') || image.includes('http://')) {
    return image;
  }

  const s3Indicators = ['/dam/', '/dam_folder/', '/poTemplate/', 's3.', 'amazonaws.com'];
  if (some(s3Indicators, (indicator) => includes(image, indicator))) {
    if (image.includes('amazonaws.com')) {
      return image;
    }
    return `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${image}`;
  }

  return `${imageUrl}/${image}`;
};

const isInEditMode = (fromColour, image) => {
  let FP;
  if (fromColour) {
    FP = `${image.data}`;
  } else if (isBase64Image(image)) {
    FP = `${image}`;
  } else {
    FP = getImagePath(image);
  }
  return FP;
};

const forImage = (isEditMode, fromColour, image) => {
  let path;
  if (isEditMode) {
    path = isInEditMode(fromColour, image);
  } else path = `${image}`;
  return path;
};

const errMsgsForExtenstions = (acceptedFiles, fromDesignDetails, fromPOTemplate, setValidatedSelectedFile, t) => {
  if (isEmpty(acceptedFiles) && (fromDesignDetails || fromPOTemplate)) {
    setValidatedSelectedFile({
      extensionError: t('IMG_DROPZONE.FILE_FORMAT_NOT_VALID'),
    });
  }
  if (acceptedFiles[0] === undefined) {
    setValidatedSelectedFile({
      extensionError: t('IMG_DROPZONE.FILE_FORMAT_NOT_VALID'),
    });
  }
};

const errMsgsForSize = (acceptedFiles, files, setValidatedSelectedFile, setLoader, setFiles, t) => {
  if (acceptedFiles.length > 0) {
    const fileSize = (acceptedFiles[0].size / 1024 / 1024).toFixed(0);
    if (fileSize > 5) {
      setValidatedSelectedFile({
        sizeError: t('IMG_DROPZONE.ATTECHEMENT_FILE_MESSAGE'),
      });

      return false;
    }
    files.length === 1 && setLoader(true);
    setValidatedSelectedFile({});
    setFiles(
      acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      ),
    );
  }
};

const showErrMsgs = (validatedSelectedFile) => {
  return (
    Object.keys(validatedSelectedFile).length !== 0 && (
      <div className="d-flex justify-content-center align-items-center">
        <span className="text-danger">
          {validatedSelectedFile.sizeError}
          {validatedSelectedFile.extensionError}
        </span>
      </div>
    )
  );
};

const names = (title) => {
  return title || '';
};

const ImgDropzone = forwardRef(
  (
    {
      onSubmit,
      image,
      isEditMode,
      acceptedFileExtenstion,
      fromColour,
      title,
      cropSize,
      fromDesignDetails,
      fromRelace,
      replaceItem,
      setSendDocument,
      setSendDesignDetail,
      fromBom,
      fromPOTemplate,
      fromProductAddEdit,
      defaultCrop,
      fromCustomModule,
      enableShiftSquareCrop = false,
      useNaturalCropResolution = false,
      variant = 'default',
    },
    ref,
  ) => {
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const dispatch = useDispatch();
    const [uploading, setUploading] = useState(false);
    const [crop, setCrop] = useState({
      unit: '%',
      width: fromPOTemplate ? 80 : 30,
      height: fromPOTemplate ? 60 : cropSize === 'FREE' && 30,
      x: fromPOTemplate ? 10 : 0,
      y: fromPOTemplate ? 20 : 0,
      aspect: cropSize === 'FREE' ? 0 : cropped(cropSize),
    });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [validatedSelectedFile, setValidatedSelectedFile] = useState({});
    const [files, setFiles] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [submit, setSubmit] = useState(false);
    const [aspect, setAspect] = useState(cropSize === 'FREE' ? undefined : 1);

    const { t } = useTranslation('common');

    const cropAtNaturalResolution = useNaturalCropResolution || Boolean(fromProductAddEdit);

    const btnNames = (fromRelace) => {
      let condString = '';
      if (fromRelace && defaultCrop) {
        condString = t('IMG_DROPZONE.CROP_IMAGE');
      } else if (fromRelace && !defaultCrop) {
        condString = t('IMG_DROPZONE.REPLACE');
      } else {
        condString = t('IMG_DROPZONE.ADD');
      }
      return condString;
    };

    const toDataUrl = (url, callback) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = function () {
        const reader = new FileReader();
        reader.onloadend = function () {
          const fileObj = base64StringtoFile(reader.result);
          callback(fileObj);
        };
        reader.readAsDataURL(xhr.response);
      };
      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.send();
    };

    useEffect(() => {
      if (!enableShiftSquareCrop) return;

      const handleKeyDown = (e) => {
        if (e.key === 'Shift') {
          setAspect(1);
        }
      };

      const handleKeyUp = (e) => {
        if (e.key === 'Shift') {
          setAspect(undefined);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
      };
    }, [enableShiftSquareCrop]);

    useEffect(() => {
      if (!enableShiftSquareCrop) return;

      setCrop((prev) => ({
        ...prev,
        aspect: aspect ?? 0,
      }));
    }, [aspect, enableShiftSquareCrop]);

    useEffect(() => {
      if (replaceItem?.image) {
        let imgCase = '';
        if (replaceItem?.image?.includes('/poTemplate/')) {
          imgCase = `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${replaceItem?.image}`;
        } else if (replaceItem?.image?.includes('https://') || replaceItem?.image?.includes('http://')) {
          imgCase = replaceItem?.image;
        } else {
          imgCase = `${imageUrl}/${replaceItem?.image}`;
        }
        let filePath = '';
        filePath = replaceItem?.image;
        toDataUrl(filePath, (file) => {
          setFiles([
            Object.assign(file, {
              preview: replaceItem?.image?.includes('/poTemplate/') ? imgCase : URL.createObjectURL(file),
            }),
          ]);
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replaceItem]);

    useEffect(() => {
      const orignalImage = async () => {
        if (fromPOTemplate && image) {
          const tempImage = await image;
          const cropImage = tempImage;
          toDataUrl(cropImage, (file) => {
            setFiles([
              Object.assign(file, {
                preview: URL.createObjectURL(file),
              }),
            ]);
          });
        }
      };
      orignalImage();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromPOTemplate, image]);

    useEffect(() => {
      if (fromPOTemplate) {
        setFiles([]);
        setValidatedSelectedFile({});
        setCompletedCrop(null);
        setCrop({
          unit: '%',
          width: 80,
          height: 60,
          x: 10,
          y: 20,
          aspect: 0,
        });
      }
    }, [fromPOTemplate]);

    async function getS3ImageAsBase64(s3ImageUrl) {
      try {
        const response = await fetch(s3ImageUrl, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        return await convertBlobToBase64(blob);
      } catch (error) {
        throw new Error(`Error fetching image from S3: ${error.message}`);
      }
    }

    function convertBlobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
    useEffect(() => {
      const handleImageLoad = async (imageUrl) => {
        try {
          const base64String = await getS3ImageAsBase64(imageUrl);
          const fileName = imageUrl.split('?')[0].split('/').pop() || 'image.png';
          const fileObj = base64StringtoFile(base64String, fileName);
          setFiles([
            Object.assign(fileObj, {
              preview: base64String,
            }),
          ]);
        } catch (error) {
          console.error('Failed to load image from S3:', error);
        }
      };

      const loader = resolveImageLoader(image, { fromRelace, fromPOTemplate, noImageAsset: noImage });

      if (loader === 's3') {
        handleImageLoad(image);
      }

      if (loader === 'xhr') {
        const filePath = forImage(isEditMode, fromColour, image);
        toDataUrl(filePath, (individualfile) => {
          setFiles([
            Object.assign(individualfile, {
              preview: URL.createObjectURL(individualfile),
            }),
          ]);
        });
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toBase64 = async (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });

    const uploadCroppedImg = async (canvas, crops) => {
      if (!crops || !canvas) {
        return;
      }
      const canvasRef = previewCanvasRef.current;
      return canvasRef.toDataURL('image/png');
    };

    const base64StringtoFile = (base64String, filename) => {
      const arr = base64String?.split(',');
      const mime = arr?.[0]?.match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    };

    /**
     * Rasterises the selected crop into the hidden preview canvas, which `uploadCroppedImg`
     * then reads back as the saved image.
     *
     * Product add/edit opts into natural resolution: the canvas is sized to the crop's size
     * in the SOURCE image's own pixels, giving a 1:1 copy of the selected region. The product
     * listing enlarges this crop in a hover preview, so a display-sized raster looked blurry.
     *
     * Every other consumer keeps the original device-pixel-ratio behaviour — the canvas is
     * sized to the crop as drawn on screen. Those modules display the crop at thumbnail size
     * and were tuned against these dimensions, so their stored images are left untouched.
     */
    useEffect(() => {
      if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
        return;
      }

      const image = imgRef.current;
      const canvas = previewCanvasRef.current;
      const crop = completedCrop;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext('2d');
      const sourceCropWidth = crop.width * scaleX;
      const sourceCropHeight = crop.height * scaleY;

      if (cropAtNaturalResolution) {
        canvas.width = Math.round(sourceCropWidth);
        canvas.height = Math.round(sourceCropHeight);

        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, sourceCropWidth, sourceCropHeight, 0, 0, sourceCropWidth, sourceCropHeight);
        return;
      }

      const pixelRatio = window.devicePixelRatio;

      canvas.width = crop.width * pixelRatio;
      canvas.height = crop.height * pixelRatio;

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, sourceCropWidth, sourceCropHeight, 0, 0, crop.width, crop.height);
    }, [completedCrop, cropAtNaturalResolution]);

    const onFileDrop = (acceptedFiles) => {
      errMsgsForExtenstions(acceptedFiles, fromDesignDetails, fromPOTemplate, setValidatedSelectedFile, t);
      errMsgsForSize(acceptedFiles, files, setValidatedSelectedFile, setLoader, setFiles, t);
    };

    const onLoad = useCallback((img) => {
      imgRef.current = img;
      setLoader(false);
    }, []);

    const handleImgLoad = () => {
      setLoader(false);
    };

    const thumbs = files?.map((file, index) =>
      files?.length === 1 ? (
        <ReactCrop
          key={file?.size}
          style={{ verticalAlign: 'bottom' }}
          src={file?.preview}
          onImageLoaded={onLoad}
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
        />
      ) : (
        <ul key={`file-list-${index}`}>
          <li style={{ listStyleType: 'none' }} key={file.name} className="d-flex align-items-center">
            <img src={file?.preview} alt="images" width={200} height={200} onLoad={handleImgLoad} />
            <button title={t('IMG_DROPZONE.DELETE')} className="btn btn-outline" onClick={() => removeImage(file.name)}>
              <DeleteIcon size={24} />
            </button>
          </li>
        </ul>
      ),
    );

    const removeImage = async (name) => {
      const array = cloneDeep(files);
      const refactoredArray = array.filter((ele) => ele.name !== name);
      setFiles([...refactoredArray]);
      onFileDrop([...refactoredArray]);
    };
    const preview = files.map((file) => {
      return (
        files.length === 1 && (
          <canvas
            key={file?.name || ''}
            ref={previewCanvasRef}
            style={{
              width: Math.round(completedCrop?.width ?? 0),
              height: Math.round(completedCrop?.height ?? 0),
            }}
          />
        )
      );
    });

    useEffect(() => {
      if (files?.length > 0) {
        files?.forEach((file) => URL.revokeObjectURL(file.preview));
      }
    }, [files]);

    useEffect(() => {
      setFiles([]);
    }, []);

    useImperativeHandle(ref, () => ({
      async showCroppedImg() {
        if (!isEmpty(files) && files.length === 1) {
          if (fromDesignDetails) {
            const orgImage = [];
            const imgRes = [];
            const allFiles = [];
            const originalFiles = [];
            if (completedCrop?.width > 0) {
              const originalImg = toBase64(files[0]);
              const imageRes = await uploadCroppedImg(previewCanvasRef?.current, completedCrop);
              const fileObj = base64StringtoFile(imageRes, files[0]?.name);
              const originalFileObj = files[0];
              orgImage.push(originalImg);
              imgRes.push(imageRes);
              allFiles.push(fileObj);
              originalFiles.push(originalFileObj);
              return { imgRes, orgImage, allFiles, originalFiles };
            }
            if (files.length > 0) {
              const originalImg = await toBase64(files[0]);
              const imageRes = await toBase64(files[0]);
              const fileObj = files[0];
              const originalFileObj = files[0];
              orgImage.push(originalImg);
              imgRes.push(imageRes);
              allFiles.push(fileObj);
              originalFiles.push(originalFileObj);
              return { imgRes, orgImage, allFiles, originalFiles };
            }
            return '';
          }
          if (fromPOTemplate) {
            if (!isEmpty(files) && files?.length === 1) {
              const originalImg = await toBase64(files[0]);

              const activeCrop = completedCrop || crop;

              if (activeCrop?.width > 0 && imgRef.current) {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const cropImage = imgRef.current;

                const pixelRatio = window.devicePixelRatio;

                const naturalCropX = activeCrop.unit === '%' ? (activeCrop.x / 100) * cropImage.naturalWidth : activeCrop.x;
                const naturalCropY = activeCrop.unit === '%' ? (activeCrop.y / 100) * cropImage.naturalHeight : activeCrop.y;
                const naturalCropWidth = activeCrop.unit === '%' ? (activeCrop.width / 100) * cropImage.naturalWidth : activeCrop.width;
                const naturalCropHeight = activeCrop.unit === '%' ? (activeCrop.height / 100) * cropImage.naturalHeight : activeCrop.height;

                canvas.width = naturalCropWidth * pixelRatio;
                canvas.height = naturalCropHeight * pixelRatio;

                ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(cropImage, naturalCropX, naturalCropY, naturalCropWidth, naturalCropHeight, 0, 0, naturalCropWidth, naturalCropHeight);

                const imageRes = canvas.toDataURL('image/png');
                return {
                  imgRes: imageRes,
                  orgImage: originalImg,
                };
              }

              return {
                imgRes: originalImg,
                orgImage: originalImg,
              };
            }
            return '';
          }
          if (completedCrop?.width > 0) {
            const originalImg = await toBase64(files[0]);
            const imageRes = await uploadCroppedImg(previewCanvasRef.current, completedCrop);
            const fileObj = base64StringtoFile(imageRes, files[0].name);
            const originalFileObj = files[0];
            return {
              imageRes,
              originalImg,
              fileObj,
              originalFileObj,
            };
          }
          if (files.length > 0) {
            const originalImg = await toBase64(files[0]);
            const imageRes = await toBase64(files[0]).catch((e) => console.log(e, 'errr'));
            const fileObj = files[0];
            const originalFileObj = files[0];
            return { imageRes, originalImg, fileObj, originalFileObj };
          }
          return '';
        }
        if (!isEmpty(files)) {
          const orgImage = [];
          const imgRes = [];
          const allFiles = [];
          const originalFiles = [];
          for (const ele of files) {
            const originalImg = await toBase64(ele);
            const imageRes = await toBase64(ele).catch((e) => console.log(e, 'errr'));
            const fileObj = ele;
            const originalFileObj = ele;
            orgImage.push(originalImg);
            imgRes.push(imageRes);
            allFiles.push(fileObj);
            originalFiles.push(originalFileObj);
          }
          return { imgRes, orgImage, allFiles, originalFiles };
        }
        return '';
      },
    }));

    const openImportModal = () => {
      setShowImportModal(true);
    };

    const hideImportingFile = async () => {
      setShowImportModal(false);
      await dispatch(action.clearDocOptions());
      await dispatch(action.resetModal());
    };

    const images = (selection) => {
      return selection?.image ? selection?.image : '';
    };

    const pathSection = (selection) => {
      return selection?.path ? selection?.path : images(selection);
    };

    const selectedFile = async (selection) => {
      const imagePath = selection?.pdf ? selection.pdf : pathSection(selection);
      let filePath = getImagePath(imagePath);
      if (selection?.docType === 'Colour' && filePath.includes('/assets/')) {
        filePath = filePath?.replace('static', '');
      }

      toDataUrl(filePath, (singlefile) => {
        setFiles([
          Object.assign(singlefile, {
            preview: URL.createObjectURL(singlefile),
          }),
        ]);
      });
      hideImportingFile();
      await dispatch(action.clearDocOptions());
      await dispatch(action.resetModal());
    };

    const imgup = () => {
      onSubmit();
      setFiles([]);
      setSubmit(true);
    };

    const uploadImage = async () => {
      if (imgRef.current === null && fromCustomModule) {
        toasterMsg(t('IMG_DROPZONE.PLEASE_UPLOAD_FILE'), 'danger');
        return false;
      }
      setUploading(true);
      await onSubmit();
      setUploading(false);
    };

    const sendToDocs = (e) => {
      setSendDocument(e?.target?.checked);
    };

    const sendToDesignDetail = (e) => {
      setSendDesignDetail(e?.target?.checked);
    };

    const toRenderDesignModal = () => {
      if (!fromDesignDetails && variant === 'asset') {
        return renderAssetVariant();
      }
      return fromDesignDetails ? (
        <BombiixModal
          show={fromDesignDetails}
          size="lg"
          className="color-way-modal"
          title={t('IMG_DROPZONE.UPLOAD_IMAGE')}
          onClose={() => onSubmit('close')}
          onSave={imgup}
          saveLabel={btnNames(fromRelace)}
          saveDisabled={Boolean(Object.values(validatedSelectedFile).length)}
        >
          <div className="bom-image-upload">
            {fromDesignDetails && !defaultCrop ? (
              <div className="d-flex justify-content-end mb-2">
                <button name="importFiles" onClick={openImportModal} className="btn btn-default" type="button">
                  {t('IMG_DROPZONE.IMPORT_FILE')}
                </button>
              </div>
            ) : null}
            {fromDesignDetails && !defaultCrop ? (
              <Dropzone accept={acceptedFileExtenstion} onDrop={onFileDrop}>
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <section className="upload-image-section" {...getRootProps()}>
                    <div className="w-100 py-5 text-center">
                      <input {...getInputProps()} />
                      {isDragActive ? (
                        <p className="mb-0"> {t('IMG_DROPZONE.DROP_FILE_HERE')}</p>
                      ) : (
                        <p className="mb-0 ">
                          {t('IMG_DROPZONE.DRAG_DROP')} {names(title)} {title?.includes('image') ? '' : 'image'} {t('IMG_DROPZONE.JPEG_FILE_MESSAGE')}
                        </p>
                      )}
                    </div>

                    <label className="upload-icon ">
                      <input {...getInputProps()} type="file" accept={acceptedFileExtenstion} className="img-Upload" />
                      <a href={() => false} className="align-items-center justify-content-center">
                        <UploadIcon size={24} />
                      </a>
                    </label>
                  </section>
                )}
              </Dropzone>
            ) : null}
            {submit && imgRef.current === null && (
              <div className="d-flex justify-content-center align-items-center">
                <span className="text-danger">{t('IMG_DROPZONE.PLEASE_SELECT_FILE')}</span>
              </div>
            )}
            {showErrMsgs(validatedSelectedFile)}
            <div className="upload-crop-section mt-3">
              <div className="crop-box">
                <label className="title">
                  {' '}
                  {fromDesignDetails && defaultCrop ? t('IMG_DROPZONE.CROP_DEFALT_IMAGE') : t('IMG_DROPZONE.UPLOAD_IMAGE_PREVIEW')}
                </label>
                <div className="image-view-box">
                  {loader && (
                    <div className="w-100 bg-dark text-center">
                      <span className="text-light">{t('IMG_DROPZONE.LOADINGS')}</span>
                    </div>
                  )}
                  <div className="d-flex flex-wrap justify-content-center">{thumbs}</div>
                  <div style={{ display: 'none' }}>{preview}</div>
                </div>
              </div>
            </div>

          </div>
        </BombiixModal>
      ) : (
        <div className="common-image-upload">
          <Dropzone accept={acceptedFileExtenstion} onDrop={onFileDrop}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <section className="upload-image-section" {...getRootProps()}>
                <div className="w-100 text-center">
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p className="mb-0">{t('IMG_DROPZONE.DROP_FILES')}</p>
                  ) : (
                    <p className="mb-0">
                      {t('IMG_DROPZONE.DRAG_DROP')} {names(title)} {title?.includes('image') ? '' : 'image'} {t('IMG_DROPZONE.JPEG_FILE_MESSAGE')}
                    </p>
                  )}
                </div>

                <label className="upload-icon ">
                  <input {...getInputProps()} type="file" accept={acceptedFileExtenstion} className="img-Upload" />
                  <a href={() => false} className="mt-16 align-items-center justify-content-center">
                    <UploadIcon size={24} />
                  </a>
                </label>
              </section>
            )}
          </Dropzone>

          {showErrMsgs(validatedSelectedFile)}

          <div className="upload-crop-section mt-3">
            <div className="crop-box">
              <label className="title">{t('IMG_DROPZONE.UPLOAD_IMAGE_PREVIEW')}</label>
              <div className="image-view-box">
                {loader && (
                  <div className="w-100 bg-dark text-center">
                    <span className="text-light">{t('IMG_DROPZONE.LOADINGS')}</span>
                  </div>
                )}
                <div className="d-flex justify-content-center">
                  <div className="col-md-5 p-0">{thumbs}</div>
                </div>
                <div style={{ display: 'none' }}>{preview}</div>
              </div>
            </div>
          </div>
          {fromProductAddEdit && (
            <div className="mt-16">
              <NewCheckbox
                id="addToDesignDetail"
                name="addToDesignDetail"
                label={t('IMG_DROPZONE.ADD_DESIGN_DETAIL')}
                onChange={sendToDesignDetail}
                disabled={isEmpty(files)}
              />
            </div>
          )}
          {fromBom && (
            <div className="checkbox-label bold mt-16">
              <input type="checkbox" id="setAddTosupportingDocs" name="setAddTosupportingDocs" onChange={sendToDocs} disabled={isEmpty(files)} />
              <label htmlFor="setAddTosupportingDocs" className="chklabel align-items-start">
                <p className="px-2">{t('IMG_DROPZONE.ADD_SUPPORTING_DOCUMENTS')}</p>
              </label>
            </div>
          )}
          {!fromProductAddEdit && (
            <div className="common-model-footer mt-16">
              <button onClick={uploadImage} type="button" className="btn p-0" disabled={Object.values(validatedSelectedFile).length}>
                {uploading ? <Spinner animation="border" /> : <TickIcon size={24} />}
              </button>
            </div>
          )}
        </div>
      );
    };

    const renderAssetVariant = () => (
      <div className="common-image-upload">
        <BombiixDropzone
          accept={acceptedFileExtenstion}
          onDrop={onFileDrop}
          preview={files.length > 0 ? thumbs : null}
          footer={loader ? <span className="bombiix-image-dropzone__hint">{t('IMG_DROPZONE.LOADINGS')}</span> : null}
        />
        {showErrMsgs(validatedSelectedFile)}
        <div className="d-none">{preview}</div>
      </div>
    );

    return (
      <>
        {fromPOTemplate ? (
          <BombiixModal
            show={fromPOTemplate}
            size="lg"
            title={t('CUSTOM_MODULE.UPLOAD_IMAGE')}
            onClose={() => onSubmit('close')}
            onSave={imgup}
            saveLabel={t('IMG_DROPZONE.UPLOAD_IMAGES')}
            saveDisabled={Boolean(Object.values(validatedSelectedFile)?.length)}
          >
            <div className="bom-image-upload">
              <Dropzone accept={acceptedFileExtenstion} onDrop={onFileDrop}>
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <section className="upload-image-section" {...getRootProps()}>
                    <div className="w-100 py-5 text-center">
                      <input {...getInputProps()} />
                      {isDragActive ? <p className="mb-0">{t('IMG_DROPZONE.DROP_FILE_HERE')}</p> : <p className="mb-0 ">{t('IMG_DROPZONE.FILE_DROP')}</p>}
                    </div>

                    <label className="upload-icon ">
                      <input {...getInputProps()} type="file" accept={acceptedFileExtenstion} className="img-Upload" />
                      <a href={() => false} className="align-items-center justify-content-center">
                        <UploadIcon size={24} />
                      </a>
                    </label>
                  </section>
                )}
              </Dropzone>
              {submit && imgRef.current === null && (
                <div className="d-flex justify-content-center align-items-center">
                  <span className="text-danger">{t('IMG_DROPZONE.PLEASE_SELECT_FILES')}</span>
                </div>
              )}
              {showErrMsgs(validatedSelectedFile)}
              <div className="upload-crop-section mt-3">
                <div className="crop-box">
                  <label className="title">{t('IMG_DROPZONE.UPLOAD_IMAGE_CROP')}</label>
                  <div className="image-view-box">
                    {loader && (
                      <div className="w-100 bg-dark text-center">
                        <span className="text-light">{t('IMG_DROPZONE.LOADING')}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-center">{thumbs}</div>
                    <div style={{ display: 'none' }}>{preview}</div>
                  </div>
                </div>
              </div>

            </div>
          </BombiixModal>
        ) : (
          toRenderDesignModal()
        )}
        {showImportModal && <ImportFileModal onOpen={showImportModal} toggle={hideImportingFile} onSubmit={selectedFile} />}
      </>
    );
  },
);

ImgDropzone.displayName = 'ImgDropzone';

export default ImgDropzone;
